import * as React from "react"
import { motion } from "motion/react"

// Chip denomination config — sorted high to low for greedy breakdown
const DENOMINATIONS = [
  { value: 500, color: "#9B59B6", border: "#7D3C98", label: "500" },
  { value: 100, color: "#1A1A1A", border: "#444444", label: "100" },
  { value: 25,  color: "#22C55E", border: "#16A34A", label: "25"  },
  { value: 10,  color: "#3B82F6", border: "#2563EB", label: "10"  },
  { value: 5,   color: "#EF4444", border: "#DC2626", label: "5"   },
  { value: 1,   color: "#F5F5F5", border: "#D4D4D4", label: "1"   },
] as const

type Denomination = (typeof DENOMINATIONS)[number]

// Greedy breakdown: convert amount into fewest chips
const getChipBreakdown = (amount: number): { denom: Denomination; count: number }[] => {
  const result: { denom: Denomination; count: number }[] = []
  let remaining = Math.max(0, Math.floor(amount))
  for (const denom of DENOMINATIONS) {
    if (remaining <= 0) break
    const count = Math.floor(remaining / denom.value)
    if (count > 0) {
      result.push({ denom, count })
      remaining -= count * denom.value
    }
  }
  return result
}

// Single poker chip disc
const Chip = ({
  denom,
  size,
  offsetY = 0,
}: {
  denom: Denomination
  size: number
  offsetY?: number
}) => {
  const isWhite = denom.value === 1
  const textColor = isWhite ? "#1A1A1A" : "#FFFFFF"
  const fontSize = size <= 16 ? 5 : 7
  const ringSize = Math.max(size - 6, 4)

  return (
    <div
      className="absolute left-0 rounded-full flex items-center justify-center"
      style={{
        width: size,
        height: size,
        bottom: offsetY,
        backgroundColor: denom.color,
        border: `2px solid ${denom.border}`,
        boxShadow: "0 1px 2px rgba(0,0,0,0.4), inset 0 1px 1px rgba(255,255,255,0.2)",
      }}
    >
      {/* Dashed inner ring for casino chip look */}
      <div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: ringSize,
          height: ringSize,
          border: `1px dashed ${denom.border}`,
          opacity: 0.5,
        }}
      />
      <span
        className="relative font-black leading-none"
        style={{
          fontSize,
          color: textColor,
          fontFamily: "Outfit, -apple-system, BlinkMacSystemFont, sans-serif",
          textShadow: isWhite ? "none" : "0 1px 1px rgba(0,0,0,0.4)",
        }}
      >
        {denom.label}
      </span>
    </div>
  )
}

// Vertical stack of a single denomination (max 10 visible chips)
const SingleStack = ({
  denom,
  count,
  size,
  animate = true,
}: {
  denom: Denomination
  count: number
  size: number
  animate?: boolean
}) => {
  const visibleCount = Math.min(count, 10)
  const chipGap = size <= 16 ? 2 : 3
  const stackHeight = size + (visibleCount - 1) * chipGap

  return animate ? (
    <motion.div
      className="relative"
      style={{ width: size, height: stackHeight }}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {Array.from({ length: visibleCount }).map((_, i) => (
        <Chip key={i} denom={denom} size={size} offsetY={i * chipGap} />
      ))}
    </motion.div>
  ) : (
    <div className="relative" style={{ width: size, height: stackHeight }}>
      {Array.from({ length: visibleCount }).map((_, i) => (
        <Chip key={i} denom={denom} size={size} offsetY={i * chipGap} />
      ))}
    </div>
  )
}

// Main export: renders grouped stacks for an amount
// size defaults to 24 (desktop). Pass 16 for mobile.
export const ChipStack = ({
  amount,
  size = 24,
  compact = false,
  animate = true,
  className = "",
}: {
  amount: number
  size?: number
  compact?: boolean
  animate?: boolean
  className?: string
}) => {
  if (amount <= 0) return null

  const breakdown = getChipBreakdown(amount)
  if (breakdown.length === 0) return null

  // compact: show only top 2 denominations (for tight spaces)
  const stacks = compact ? breakdown.slice(0, 2) : breakdown

  return (
    <div className={`flex items-end gap-0.5 ${className}`}>
      {stacks.map(({ denom, count }) => (
        <SingleStack
          key={denom.value}
          denom={denom}
          count={count}
          size={size}
          animate={animate}
        />
      ))}
    </div>
  )
}

// Convenience: responsive version that switches size at md breakpoint
export const ResponsiveChipStack = ({
  amount,
  compact = false,
  animate = true,
  className = "",
}: {
  amount: number
  compact?: boolean
  animate?: boolean
  className?: string
}) => {
  if (amount <= 0) return null

  return (
    <>
      <div className={`md:hidden ${className}`}>
        <ChipStack amount={amount} size={16} compact={compact} animate={animate} />
      </div>
      <div className={`hidden md:block ${className}`}>
        <ChipStack amount={amount} size={24} compact={compact} animate={animate} />
      </div>
    </>
  )
}

export default ChipStack
